import { Theme, styled } from '@material-ui/core';
import { useState } from 'react';
import { IconArrow } from '../icons';
import { NkDropdown } from '../nkDropdown';
import { NkSelectMenuProps, NkSelectProps } from './NkSelectV2.types';
import {
  DEFAULT_INPUT_WIDTH,
  NkInputError,
  NkInputLabel,
  getInputBorderColor,
  nkInputSize,
} from '../../shared/FormControls';

export const NkSelectV2 = <T,>({
  items,
  required = false,
  disabled = false,
  value,
  testId,
  label,
  placeholder,
  errorMessage,
  onChange,
  mapListItem,
  mapValueItem,
}: NkSelectProps<T>) => {
  const [selectValue, setSelectValue] = useState(value);
  const [isOpen, setIsOpen] = useState(false);

  const handleItemClick = (item: T) => {
    setSelectValue(item);
    setIsOpen(false);

    if (onChange) {
      onChange(item);
    }
  };

  return (
    <StyledContentWrapper data-testid={testId} $disabled={disabled}>
      <NkInputLabel>
        {label} {required && '*'}
      </NkInputLabel>
      <NkDropdown
        position="bottom"
        onToggle={setIsOpen}
        key={isOpen + ''}
        isOpen={isOpen}
        menu={
          <SelectMenu
            items={items}
            mapListItem={mapListItem}
            onItemClick={handleItemClick}
          />
        }
      >
        <StyledValueWrapper $disabled={disabled} $active={isOpen}>
          {!selectValue ? (
            <StyledPlaceholder $disabled={disabled}>
              {placeholder}
            </StyledPlaceholder>
          ) : (
            <StyledValue>
              {mapValueItem ? mapValueItem(selectValue) : selectValue}
            </StyledValue>
          )}
          <StyledArrow $isOpen={isOpen} />
        </StyledValueWrapper>
      </NkDropdown>
      <NkInputError>{errorMessage}</NkInputError>
    </StyledContentWrapper>
  );
};

export const StyledContentWrapper = styled('div')<
  Theme,
  { $disabled: boolean }
>(({ theme, $disabled }) => ({
  width: DEFAULT_INPUT_WIDTH,
  display: 'flex',
  flexFlow: 'column nowrap',

  ...($disabled ? { pointerEvents: 'none' } : {}),
}));

const SelectMenu = <T,>({
  items,
  mapListItem,
  onItemClick,
}: NkSelectMenuProps<T>) => {
  return (
    <StyledMenu>
      {items.map((item, index) => (
        <StyledItem
          key={index}
          onClick={() => {
            onItemClick(item);
          }}
        >
          {mapListItem ? mapListItem(item) : item}
        </StyledItem>
      ))}
    </StyledMenu>
  );
};

const StyledMenu = styled('div')(({ theme }) => ({
  backgroundColor: theme.palette.background.default,
  width: DEFAULT_INPUT_WIDTH,
  maxHeight: '200px',
  overflowY: 'auto',
}));

const StyledItem = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  cursor: 'pointer',
  ...nkInputSize,

  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

const StyledValue = styled('div')({
  fontFamily: 'Arial',
  fontSize: '16px',
  fontStyle: 'normal',
  fontWeight: 400,
});

const StyledPlaceholder = styled('div')<Theme, { $disabled: boolean }>(
  ({ theme, $disabled }) => ({
    // TODO: change to theme props
    ...($disabled ? { color: '#dbdbdb' } : { color: '#4D4D4D' }),
  }),
);

const StyledArrow = styled(IconArrow)<Theme, { $isOpen: boolean }>(
  ({ theme, $isOpen }) => ({
    marginLeft: '14px',
    width: '15px',
    color: theme.palette.primary.light,

    ...($isOpen ? { transform: 'rotate(180deg)' } : {}),
  }),
);

const StyledValueWrapper = styled('div')<
  Theme,
  { $disabled?: boolean; $active?: boolean }
>(({ theme, $disabled, $active }) => ({
  ...nkInputSize,

  border: `1px solid ${theme.palette.divider}`,
  borderRadius: '3px',
  '&:hover': {
    borderColor: getInputBorderColor(theme, !!$disabled, true),
  },
  ...($active
    ? { borderColor: getInputBorderColor(theme, !!$disabled, false, true) }
    : {}),
  ...($disabled
    ? { borderColor: getInputBorderColor(theme, !!$disabled, false, $active) }
    : {}),

  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
}));
